import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { GIDEON_LAUNCH_IMAGES } from '../../data/gideonMedia';

const GideonLaunchCarousel = () => {
  const { t } = useLanguage();
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const total = GIDEON_LAUNCH_IMAGES.length;

  const next = useCallback(() => setActive((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setActive((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (lightboxOpen) return undefined;
    const id = window.setInterval(next, 6500);
    return () => window.clearInterval(id);
  }, [next, lightboxOpen]);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxOpen, next, prev]);

  const altBase = t('gideon.media.altBase', 'Gideon Force launch — Chaguanas');
  const current = GIDEON_LAUNCH_IMAGES[active];

  const lightboxContent = (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-black/92 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={t('gideon.media.viewLarger', 'View full size')}
      onClick={() => setLightboxOpen(false)}
    >
      <button
        type="button"
        onClick={() => setLightboxOpen(false)}
        className="absolute right-3 top-3 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-2xl leading-none text-white hover:bg-white/20 border border-white/20"
        aria-label={t('gideon.media.closeLightbox', 'Close')}
      >
        ×
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          prev();
        }}
        className="absolute left-2 sm:left-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 border border-white/20"
        aria-label={t('gideon.media.prev', 'Previous photo')}
      >
        ‹
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          next();
        }}
        className="absolute right-2 sm:right-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 border border-white/20"
        aria-label={t('gideon.media.next', 'Next photo')}
      >
        ›
      </button>
      <img
        src={current.src}
        alt={`${altBase} (${current.index})`}
        className="relative z-10 max-h-[min(90vh,1200px)] max-w-full object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        loading="eager"
        decoding="async"
      />
      <p className="relative z-10 mt-3 text-center text-sm text-white/70">
        {active + 1} / {total}
      </p>
    </div>
  );

  return (
    <div className="relative rounded-2xl overflow-hidden border border-gideon-khaki/40 shadow-xl bg-gideon-ink">
      <div className="relative aspect-[16/10] sm:aspect-[21/9] max-h-[min(70vh,520px)] bg-gideon-moss/50">
        {GIDEON_LAUNCH_IMAGES.map((item, i) => (
          <img
            key={item.src}
            src={item.src}
            alt={`${altBase} (${item.index})`}
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              i === active ? 'opacity-100 z-[1]' : 'opacity-0 z-0'
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-gideon-ink/90 via-gideon-ink/20 to-transparent z-[2] pointer-events-none" />
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className="absolute inset-0 z-[3] cursor-zoom-in bg-transparent border-0 p-0"
          aria-label={t('gideon.media.viewLarger', 'View full size')}
        />
        <div className="absolute bottom-0 left-0 right-0 z-[4] p-4 sm:p-6 pointer-events-none">
          <p className="text-gideon-parchment/90 text-xs sm:text-sm font-medium uppercase tracking-widest">
            {t('gideon.media.badge', 'Chaguanas launch')}
          </p>
          <p className="text-white text-lg sm:text-xl font-bold mt-1">
            {t('gideon.media.carouselCaption', 'The force assembled')}
          </p>
          <p className="text-white/70 text-xs sm:text-sm mt-2 opacity-90">
            {t('gideon.media.viewLarger', 'View full size')}
          </p>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            prev();
          }}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-[5] w-10 h-10 rounded-full bg-gideon-ink/60 hover:bg-gideon-ink/80 text-white flex items-center justify-center border border-white/10"
          aria-label={t('gideon.media.prev', 'Previous photo')}
        >
          ‹
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            next();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-[5] w-10 h-10 rounded-full bg-gideon-ink/60 hover:bg-gideon-ink/80 text-white flex items-center justify-center border border-white/10"
          aria-label={t('gideon.media.next', 'Next photo')}
        >
          ›
        </button>
      </div>
      <div className="flex justify-center gap-1.5 py-3 bg-gideon-ink/80 border-t border-gideon-khaki/30">
        {GIDEON_LAUNCH_IMAGES.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setActive(i)}
            className={`h-2 rounded-full transition-all ${
              i === active ? 'w-6 bg-gideon-khaki' : 'w-2 bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`${t('gideon.media.goTo', 'Go to slide')} ${i + 1}`}
            aria-current={i === active ? 'true' : undefined}
          />
        ))}
      </div>
      {typeof document !== 'undefined' &&
        lightboxOpen &&
        createPortal(lightboxContent, document.body)}
    </div>
  );
};

export default GideonLaunchCarousel;
